import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { COMPANY, SERVICES } from "@/data/site";

const PROCESS = [
  { num: "01", title: "Free Quote", text: "Call or message us — we visit, measure up and give you an honest, fixed quote." },
  { num: "02", title: "Preparation", text: "We protect your floors and furniture, fill cracks, sand and prime every surface." },
  { num: "03", title: "The Finish", text: "Even coats, razor-sharp edges, premium paints — applied with craftsman care." },
  { num: "04", title: "Clean Handover", text: "Full clean-up and a walkthrough with you before we call it done." },
];

export default function Services() {
  return (
    <main data-testid="services-page" className="pt-20">
      <Seo
        title="Services — Eunice 25 | Interior Walls, Ceilings, Frames, Skirting, Staining"
        siteName="Eunice 25"
        description="Interior wall painting, ceiling painting, door & window frame painting, skirting boards, staining & varnishing in Oldham. Free quotes — +44 7474 891884."
      />

      <section className="bg-[#121212] text-[#fbf9f5] grain relative py-24 sm:py-32" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="font-mono2 text-xs tracking-[0.35em] uppercase text-[#c8102e]">Our Service</p>
            <h1 className="mt-4 font-display font-black tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl max-w-3xl">
              Five crafts. <span className="italic text-[#c8102e]">One standard: perfect.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base sm:text-lg text-[#fbf9f5]/70 leading-relaxed">
              From a single feature wall to a full home refresh — every job gets the same
              preparation, precision and pride.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-[#fbf9f5] py-20 sm:py-28" data-testid="services-list">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 space-y-6">
          {SERVICES.map((s, i) => (
            <Reveal key={s.id} delay={0.05}>
              <article
                data-testid={`service-card-${s.id}`}
                className={`service-row group border border-black/10 rounded-2xl overflow-hidden grid md:grid-cols-[1fr_320px] bg-white`}
              >
                <div className="p-8 sm:p-12">
                  <span className="sr-num font-display font-black text-5xl text-outline-ink transition-colors">{s.id}</span>
                  <h2 className="sr-title mt-5 font-display font-bold tracking-tight text-xl sm:text-2xl lg:text-3xl transition-colors">
                    {s.title}
                  </h2>
                  <p className="sr-desc mt-4 text-base text-neutral-600 leading-relaxed max-w-xl transition-colors">
                    {s.desc}
                  </p>
                  <ul className="sr-desc mt-6 space-y-2 text-sm text-neutral-600 transition-colors">
                    {["Free written quote", "Premium trade paints", "Full clean-up included"].map((f) => (
                      <li key={f} className="flex items-center gap-2">
                        <Check className="w-4 h-4 text-[#c8102e]" /> {f}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="relative h-56 md:h-auto overflow-hidden">
                  <img
                    src={s.img}
                    alt={s.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-[#f3efe6] py-24 sm:py-32 border-y border-black/10" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="font-mono2 text-xs tracking-[0.35em] uppercase text-[#c8102e]">How We Work</p>
            <h2 className="mt-4 font-display font-black tracking-tight leading-snug text-2xl sm:text-3xl lg:text-4xl">
              From first call to <span className="italic text-[#c8102e]">final walkthrough.</span>
            </h2>
          </Reveal>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-black/10 border border-black/10">
            {PROCESS.map((p, i) => (
              <Reveal key={p.num} delay={i * 0.1} className="bg-[#f3efe6]">
                <div className="p-8 h-full" data-testid={`process-step-${p.num}`}>
                  <span className="font-mono2 text-sm text-[#c8102e]">{p.num}</span>
                  <h3 className="mt-3 font-bold text-lg tracking-tight">{p.title}</h3>
                  <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-14 text-center">
              <Link
                to="/contact"
                data-testid="services-quote-btn"
                className="group inline-flex items-center gap-2 bg-[#c8102e] hover:bg-[#a00d24] text-white font-bold px-10 py-4 rounded-full transition-colors"
              >
                Get Your Free Quote
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <p className="mt-4 font-mono2 text-xs tracking-[0.2em] uppercase text-neutral-500">
                or call {COMPANY.phone1}
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
